// Inserting images (spec §5.7): decode once, encode 256 / 1024 / original
// (≤ 4096) variants as WebP (PNG where the browser can't encode WebP), hash the
// original, place the object straight away using the local bitmap, then
// upload. Other people see a placeholder until the upload lands.
import type { Image } from '../../shared/types.ts';
import { IMAGE_VARIANTS, type ImageVariant } from '../../shared/api.ts';
import { newId } from '../../shared/ids.ts';
import type { Editor, ImageUploader } from '../editor.ts';

const WEBP_QUALITY = 0.86;
const STAGGER = 40; // world units between several dropped images

interface Encoded { name: ImageVariant; blob: Blob; w: number; h: number }

async function encode(source: ImageBitmap, name: ImageVariant, maxSide: number): Promise<Encoded & { canvas: OffscreenCanvas }> {
  const k = Math.min(1, maxSide / Math.max(source.width, source.height));
  const w = Math.max(1, Math.round(source.width * k));
  const h = Math.max(1, Math.round(source.height * k));
  const canvas = new OffscreenCanvas(w, h);
  const ctx = canvas.getContext('2d')!;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(source, 0, 0, w, h);
  const blob = await canvas.convertToBlob({ type: 'image/webp', quality: WEBP_QUALITY });
  return { name, blob, w, h, canvas };
}

async function sha256(blob: Blob): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await blob.arrayBuffer());
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

async function upload(workspaceId: string, hash: string, v: Encoded): Promise<void> {
  const res = await fetch(`/api/workspaces/${workspaceId}/assets`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': v.blob.type,
      'X-Asset-Hash': hash,
      'X-Asset-Variant': v.name,
      'X-Asset-Width': String(v.w),
      'X-Asset-Height': String(v.h),
    },
    body: v.blob,
  });
  if (!res.ok) throw new Error(((await res.json().catch(() => ({}))) as { error?: string }).error ?? `upload failed (${res.status})`);
}

export function createImageUploader(ed: Editor, workspaceId: string, onError: (message: string) => void): ImageUploader {
  return (files, at) => void insert(files, at);

  async function insert(files: File[], at: [number, number] | null): Promise<void> {
    const view = ed.viewport();
    let [x, y] = at ?? [view.x + view.w / 2, view.y + view.h / 2];
    const created: string[] = [];
    for (const file of files) {
      let id: string | null = null;
      try {
        const source = await createImageBitmap(file);
        const variants = await Promise.all(IMAGE_VARIANTS.map((v) => encode(source, v.name, v.size)));
        source.close();
        const original = variants.find((v) => v.name === 'original')!;
        const hash = await sha256(original.blob);
        ed.images.seed(hash, 'original', original.canvas.transferToImageBitmap());

        // Show it at most half the viewport across, never larger than it is.
        const k = Math.min(1, (Math.min(view.w, view.h) * 0.5) / Math.max(original.w, original.h));
        const w = original.w * k, h = original.h * k;
        const obj: Image = {
          id: newId(), type: 'image', x: x - w / 2, y: y - h / 2, w, h, rotation: 0,
          z: ed.zKeysOnTop(1)[0]!, createdBy: ed.userId, assetHash: hash,
        };
        ed.apply([{ t: 'create', obj }]);
        id = obj.id;
        created.push(id);
        // Selected as soon as it's placed, so it can be moved or deleted while it uploads.
        ed.setTool('select');
        ed.setSelection(created.filter((c) => ed.store.has(c)));
        x += STAGGER;
        y += STAGGER;

        await upload(workspaceId, hash, original); // first: the server checks its hash
        await Promise.all(variants.filter((v) => v !== original).map((v) => upload(workspaceId, hash, v)));
      } catch (err) {
        if (id) ed.apply([{ t: 'delete', id }]);
        onError(`Couldn't add ${file.name}: ${(err as Error).message}`);
      }
    }
  }
}
